// WYVIO UI 1.0 — Mandat Cockpit (Lot M1)
// Route canonique : /app/mandat/:id
// Squelette 5 sections vides marquées "à venir en M2-M8".

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Copy, RefreshCw, Link2, Ban, FileSignature,
  User, Car, Euro, Megaphone, LineChart, MessageCircle, History,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";
import { mandatesApi, sellersApi } from "@/lib/api/mandates";
import mandateTransitions from "@/lib/api/mandateTransitions";
import mandatePricing from "@/lib/api/mandatePricing";
import mandatePublications from "@/lib/api/mandatePublications";
import mandatePerformance from "@/lib/api/mandatePerformance";
import StatusPill from "@/components/mandats/StatusPill";
import TransitionMenu from "@/components/mandats/TransitionMenu";
import CloseMandateDialog from "@/components/mandats/CloseMandateDialog";
import AuditTimeline from "@/components/mandats/AuditTimeline";
import PricingCard from "@/components/mandats/PricingCard";
import InitializePricingDialog from "@/components/mandats/InitializePricingDialog";
import UpdateAskingPriceDialog from "@/components/mandats/UpdateAskingPriceDialog";
import PricingHistoryTimeline from "@/components/mandats/PricingHistoryTimeline";
import PublicationsCard from "@/components/mandats/PublicationsCard";
import AddChannelDialog from "@/components/mandats/AddChannelDialog";
import PublicationEventsTimeline from "@/components/mandats/PublicationEventsTimeline";
import PerformanceCard from "@/components/mandats/PerformanceCard";
import SnapshotDialog from "@/components/mandats/SnapshotDialog";
import UpdateFrequencyDialog from "@/components/mandats/UpdateFrequencyDialog";
import PerformanceTimeline from "@/components/mandats/PerformanceTimeline";

const STATUS_LABEL = {
  draft: "Brouillon", signed: "Signé", ready: "Prêt à commercialiser",
  commercializing: "En commercialisation", reserved: "Réservé", sold: "Vendu",
  expired: "Échu", withdrawn: "Retiré", sold_by_seller: "Vendu (vendeur)",
  sold_elsewhere: "Vendu (ailleurs)", price_disagreement: "Désaccord tarifaire",
  agency_termination: "Dénoncé (agence)", abandoned: "Abandonné", other: "Clôture · autre",
};
const STATUS_DOT = {
  draft: "#94A3B8", signed: "#38BDF8", ready: "#F59E0B",
  commercializing: "#C8FF2F", reserved: "#8B5CF6", sold: "#10B981",
  expired: "#64748B", withdrawn: "#64748B", sold_by_seller: "#64748B",
  sold_elsewhere: "#64748B", price_disagreement: "#64748B",
  agency_termination: "#64748B", abandoned: "#64748B", other: "#64748B",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const vehicleTitle = (v = {}) => {
  const bits = [v.brand, v.model, v.version].filter(Boolean);
  return bits.join(" ") || "Véhicule à renseigner";
};

const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copié`);
  } catch {
    toast.error("Impossible de copier");
  }
};

// ═════════════ Section skeleton (à venir M2-M8) ═════════════

const SectionCard = ({ icon: Icon, title, milestone, description, children, testId }) => (
  <section
    className="rounded-3xl bg-white/60 p-7"
    style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
          <Icon size={16} strokeWidth={1.7} className="text-black/60" />
        </div>
        <div>
          <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-black">{title}</h2>
          {description && (
            <p className="mt-1 text-[12.5px] text-black/50 max-w-[420px]">{description}</p>
          )}
        </div>
      </div>
      {milestone && (
        <span
          className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40"
          data-testid={`${testId}-milestone`}
        >
          {milestone}
        </span>
      )}
    </div>
    {children && <div className="mt-5">{children}</div>}
  </section>
);

// ═════════════ MAIN ═════════════

export default function MandatCockpitV3() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const [mandate, setMandate] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // M2 · audit + closure dialog
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  // M3 · pricing
  const [pricingHistory, setPricingHistory] = useState([]);
  const [pricingHistoryLoading, setPricingHistoryLoading] = useState(false);
  const [initPricingOpen, setInitPricingOpen] = useState(false);
  const [updateAskingOpen, setUpdateAskingOpen] = useState(false);
  // M4 · publications
  const [pubEvents, setPubEvents] = useState([]);
  const [pubEventsLoading, setPubEventsLoading] = useState(false);
  const [pubCatalog, setPubCatalog] = useState([]);
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  // M5 · performance
  const [perfSummary, setPerfSummary] = useState(null);
  const [perfSnapshots, setPerfSnapshots] = useState([]);
  const [perfLoading, setPerfLoading] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [supersedeTarget, setSupersedeTarget] = useState(null);   // snapshot ou null
  const [freqDialogOpen, setFreqDialogOpen] = useState(false);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const evts = await mandateTransitions.audit(id);
      setAudit(evts);
    } catch (e) {
      // Silent — pas critique
    } finally {
      setAuditLoading(false);
    }
  };

  const loadPricingHistory = async () => {
    setPricingHistoryLoading(true);
    try {
      const evts = await mandatePricing.history(id);
      setPricingHistory(evts);
    } catch (e) {
      // Silent
    } finally {
      setPricingHistoryLoading(false);
    }
  };

  const loadPublicationEvents = async () => {
    setPubEventsLoading(true);
    try {
      const evts = await mandatePublications.events(id);
      setPubEvents(evts);
    } catch (e) {
      // Silent
    } finally {
      setPubEventsLoading(false);
    }
  };

  const loadPublicationCatalog = async () => {
    try {
      const cat = await mandatePublications.catalog();
      setPubCatalog(cat);
    } catch (e) {
      // Silent
    }
  };

  const loadPerformance = async () => {
    setPerfLoading(true);
    try {
      const [summary, snaps] = await Promise.all([
        mandatePerformance.summary(id),
        mandatePerformance.list(id, true),
      ]);
      setPerfSummary(summary);
      setPerfSnapshots(snaps);
    } catch (e) {
      // Silent
    } finally {
      setPerfLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const m = await mandatesApi.get(id);
      setMandate(m);
      if (m?.seller_id) {
        try {
          const s = await sellersApi.get(m.seller_id);
          setSeller(s);
        } catch { setSeller(null); }
      }
    } catch (e) {
      toast.error(e.message || "Mandat introuvable");
      setMandate(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); loadAudit(); loadPricingHistory();
    loadPublicationEvents(); loadPublicationCatalog();
    loadPerformance();
  }, [id]);

  const doAddChannel = async (payload) => {
    try {
      const updated = await mandatePublications.addChannel(id, payload);
      setMandate(updated);
      setAddChannelOpen(false);
      toast.success(`Canal ${payload.channel} ajouté`);
      await loadPublicationEvents();
      await loadPerformance();
    } catch (e) {
      if (e.code === "mandate_not_commercializing") {
        toast.error("Le mandat doit être en commercialisation");
      } else if (e.code === "channel_already_exists") {
        toast.error("Canal déjà présent");
      } else if (e.code === "invalid_channel_slug") {
        toast.error("Slug invalide (kebab-case lowercase)");
      } else {
        toast.error(e.message || "Ajout impossible");
      }
    }
  };

  const doActivateChannel = async (channel) => {
    try {
      const updated = await mandatePublications.activate(id, channel);
      setMandate(updated);
      toast.success(`${channel} publié`);
      await loadPublicationEvents();
      await loadPerformance();
    } catch (e) {
      toast.error(e.message || "Publication impossible");
    }
  };

  const doDeactivateChannel = async (channel) => {
    if (!window.confirm(`Retirer ${channel} de la publication ?`)) return;
    try {
      const updated = await mandatePublications.deactivate(id, channel);
      setMandate(updated);
      toast.success(`${channel} retiré`);
      await loadPublicationEvents();
      await loadPerformance();
    } catch (e) {
      toast.error(e.message || "Retrait impossible");
    }
  };

  const doInitializePricing = async ({ asking_price, net_seller, currency }) => {
    try {
      const updated = await mandatePricing.initialize(id, {
        asking_price, net_seller, currency,
      });
      setMandate(updated);
      setInitPricingOpen(false);
      toast.success("Pricing initialisé");
      await loadPricingHistory();
    } catch (e) {
      if (e.code === "asking_price_below_net_seller") {
        toast.error("Le prix affiché doit être ≥ au net vendeur");
      } else if (e.code === "already_initialized") {
        toast.error("Pricing déjà initialisé");
      } else {
        toast.error(e.message || "Initialisation impossible");
      }
    }
  };

  const doUpdateAsking = async ({ value, reason }) => {
    try {
      const updated = await mandatePricing.patchAskingPrice(id, { value, reason });
      setMandate(updated);
      setUpdateAskingOpen(false);
      toast.success("Prix affiché mis à jour");
      await loadPricingHistory();
    } catch (e) {
      if (e.code === "asking_price_below_net_seller") {
        toast.error("Nouveau prix < net vendeur — proposition M7 requise");
      } else if (e.code === "reason_required") {
        toast.error("Motif requis");
      } else {
        toast.error(e.message || "Mise à jour impossible");
      }
    }
  };

  const doTransition = async (transitionKey) => {
    setBusy(true);
    try {
      const updated = await mandateTransitions.apply(id, transitionKey);
      setMandate(updated);
      toast.success("Mandat mis à jour");
      await loadAudit();
      await loadPublicationEvents();
      await loadPerformance();
    } catch (e) {
      if (e.code === "active_publications_remaining") {
        toast.error("Désactivez d'abord les canaux publiés");
      } else {
        toast.error(e.message || "Transition impossible");
      }
    } finally {
      setBusy(false);
    }
  };

  const doCreateSnapshot = async (payload) => {
    try {
      await mandatePerformance.create(id, payload);
      setSnapshotOpen(false);
      toast.success(`Snapshot période #${payload.period_index} capturé`);
      await loadPerformance();
      await load();
    } catch (e) {
      if (e.code === "period_index_gap") {
        toast.error(`Période attendue : #${e.detail?.expected} (reçu #${e.detail?.received})`);
      } else if (e.code === "period_not_ended") {
        toast.error("La période courante n'est pas encore terminée");
      } else if (e.code === "no_first_publication") {
        toast.error("Publier au moins un canal d'abord");
      } else if (e.code === "invalid_metric_value") {
        toast.error(`Valeur invalide sur ${e.detail?.metric} (${e.detail?.channel})`);
      } else {
        toast.error(e.message || "Snapshot impossible");
      }
    }
  };

  const doSupersedeSnapshot = async (payload) => {
    if (!supersedeTarget) return;
    try {
      await mandatePerformance.supersede(id, supersedeTarget.id, payload);
      setSupersedeTarget(null);
      toast.success("Nouvelle révision créée");
      await loadPerformance();
      await load();
    } catch (e) {
      if (e.code === "snapshot_already_superseded") {
        toast.error("Cette révision a déjà été supersédée");
      } else if (e.code === "invalid_metric_value") {
        toast.error(`Valeur invalide sur ${e.detail?.metric} (${e.detail?.channel})`);
      } else {
        toast.error(e.message || "Correction impossible");
      }
    }
  };

  const doUpdateFrequency = async (days) => {
    try {
      await mandatePerformance.updateFrequency(id, days);
      setFreqDialogOpen(false);
      toast.success(`Cadence mise à jour · ${days} j`);
      await loadPerformance();
      await load();
    } catch (e) {
      if (e.code === "invalid_frequency") {
        toast.error(`Cadence hors bornes (${e.detail?.min}–${e.detail?.max} j)`);
      } else {
        toast.error(e.message || "Cadence impossible");
      }
    }
  };

  const doClose = async ({ reason, comment }) => {
    setBusy(true);
    try {
      const updated = await mandateTransitions.close(id, { reason, comment });
      setMandate(updated);
      setCloseDialogOpen(false);
      toast.success("Mandat clôturé");
      await loadAudit();
    } catch (e) {
      toast.error(e.message || "Clôture impossible");
    } finally {
      setBusy(false);
    }
  };

  const regenerateCode = async () => {
    setBusy(true);
    try {
      const r = await mandatesApi.regenerateCode(id);
      toast.success("Nouveau code généré · l'ancien est immédiatement invalide");
      setMandate((m) => ({ ...m, access_code: r.access_code,
                           access_code_updated_at: r.access_code_updated_at }));
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const generateMagicLink = async () => {
    setBusy(true);
    try {
      const r = await mandatesApi.generateMagicLink(id);
      await navigator.clipboard.writeText(r.url).catch(() => {});
      toast.success("Lien vendeur généré et copié (30 jours)");
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const revokeAccess = async () => {
    if (!window.confirm("Révoquer tous les liens d'accès vendeur actifs ?")) return;
    setBusy(true);
    try {
      const r = await mandatesApi.revokeAccess(id);
      toast.success(`${r.revoked} lien(s) révoqué(s)`);
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]" data-testid="mandat-cockpit-loading">
        <AppSidebar agency={agency} user={user} />
        <main className="lg:pl-[260px] p-10">
          <div className="mx-auto max-w-[1120px] space-y-6">
            <div className="h-8 w-32 animate-pulse rounded-full bg-black/[0.05]" />
            <div className="h-24 animate-pulse rounded-2xl bg-black/[0.03]" />
            <div className="h-40 animate-pulse rounded-3xl bg-black/[0.03]" />
          </div>
        </main>
      </div>
    );
  }

  if (!mandate) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]" data-testid="mandat-cockpit-not-found">
        <AppSidebar agency={agency} user={user} />
        <main className="lg:pl-[260px] p-10">
          <div className="mx-auto max-w-[1120px] py-16 text-center text-black/60">
            Mandat introuvable.
            <button onClick={() => navigate("/app/mandats")}
                    className="ml-3 underline">retour à la liste</button>
          </div>
        </main>
      </div>
    );
  }

  const status = mandate.status || "draft";

  return (
    <div
      className="relative min-h-screen text-black"
      style={{
        background:
          "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)",
      }}
      data-testid="mandat-cockpit-page"
    >
      <AppSidebar agency={agency} user={user} testId="mandat-cockpit-sidebar" />

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1120px] px-10 py-12">
          {/* Header */}
          <button
            onClick={() => navigate("/app/mandats")}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-black/50 hover:text-black"
            data-testid="mandat-back-btn"
          >
            <ArrowLeft size={13} /> Mandats
          </button>

          <div className="mt-4 flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-black/55">
                <StatusPill status={status} testId="mandat-header-status-pill" />
                <span className="font-mono normal-case tracking-normal text-black/50">
                  {mandate.reference}
                </span>
              </div>
              <h1 className="mt-3 text-[38px] font-semibold leading-[1.02] tracking-[-0.02em] text-black">
                {vehicleTitle(mandate.vehicle)}
              </h1>
              <p className="mt-1.5 text-[14px] text-black/50">
                {seller?.name || "Vendeur"} · Créé le {fmtDate(mandate.created_at)}
                {mandate.closed_at && (
                  <> · Clôturé le {fmtDate(mandate.closed_at)}</>
                )}
              </p>
            </div>

            <div className="flex flex-shrink-0 items-center gap-3">
              <TransitionMenu
                allowedNext={mandate.allowed_next || []}
                canClose={mandate.can_close}
                isTerminal={mandate.is_terminal}
                busy={busy}
                onTransition={doTransition}
                onCloseClick={() => setCloseDialogOpen(true)}
              />
            </div>
          </div>

          {/* Accès vendeur — la section fonctionnelle de M1 */}
          <section
            className="mt-10 rounded-3xl bg-white/70 p-7"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
            data-testid="mandat-access-card"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[16px] font-semibold tracking-[-0.01em]">
                  Accès vendeur
                </h2>
                <p className="mt-1 text-[12.5px] text-black/50 max-w-[500px]">
                  Le vendeur se connecte sans compte via <b>magic-link 30 jours</b> ou avec sa
                  référence + code d&apos;accès à 6 caractères.
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div
                className="rounded-2xl bg-white p-5"
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}
              >
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                  Référence
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[18px] font-medium tracking-[-0.01em]"
                        data-testid="mandat-reference-value">
                    {mandate.reference}
                  </span>
                  <button
                    onClick={() => copyToClipboard(mandate.reference, "Référence")}
                    className="rounded-full p-2 text-black/50 hover:bg-black/[0.04] hover:text-black"
                    data-testid="mandat-copy-reference-btn"
                    aria-label="Copier la référence"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>

              <div
                className="rounded-2xl bg-white p-5"
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}
              >
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                  Code d&apos;accès
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[18px] font-medium tracking-[-0.01em]"
                        data-testid="mandat-access-code-value">
                    {mandate.access_code || "—"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copyToClipboard(mandate.access_code, "Code")}
                      className="rounded-full p-2 text-black/50 hover:bg-black/[0.04] hover:text-black"
                      data-testid="mandat-copy-code-btn"
                      aria-label="Copier le code"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={regenerateCode}
                      disabled={busy}
                      className="rounded-full p-2 text-black/50 hover:bg-black/[0.04] hover:text-black disabled:opacity-30"
                      data-testid="mandat-rotate-code-btn"
                      aria-label="Régénérer le code"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={generateMagicLink}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                data-testid="mandat-magic-link-btn"
              >
                <Link2 size={13} /> Générer un lien vendeur (30 j)
              </button>
              <button
                onClick={revokeAccess}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[12.5px] font-medium text-black/70 transition-colors hover:text-black disabled:opacity-40"
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
                data-testid="mandat-revoke-access-btn"
              >
                <Ban size={13} /> Révoquer l&apos;accès
              </button>
            </div>
          </section>

          {/* 5 sections skeleton */}
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <SectionCard
              icon={User}
              title="Vendeur"
              milestone="M1"
              description="Identité du vendeur et coordonnées."
              testId="mandat-section-seller"
            >
              <dl className="space-y-2 text-[13px]">
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Nom</dt>
                  <dd className="text-black">{seller?.name || "—"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Email</dt>
                  <dd className="text-black" data-testid="mandat-seller-email">
                    {seller?.email || "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Téléphone</dt>
                  <dd className="text-black">{seller?.phone || "—"}</dd>
                </div>
              </dl>
            </SectionCard>

            <SectionCard
              icon={Car}
              title="Véhicule"
              milestone="M1"
              description="Fiche véhicule commercialisée (1 mandat = 1 véhicule)."
              testId="mandat-section-vehicle"
            >
              <dl className="space-y-2 text-[13px]">
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Marque · Modèle</dt>
                  <dd className="text-black">{vehicleTitle(mandate.vehicle)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Année</dt>
                  <dd className="text-black">{mandate.vehicle?.year || "—"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Kilométrage</dt>
                  <dd className="text-black">
                    {typeof mandate.vehicle?.mileage === "number"
                      ? `${mandate.vehicle.mileage.toLocaleString("fr-FR")} km`
                      : "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-black/45">Énergie</dt>
                  <dd className="text-black">{mandate.vehicle?.energy || "—"}</dd>
                </div>
              </dl>
            </SectionCard>

            <SectionCard
              icon={Euro}
              title="Prix & net vendeur"
              milestone="M3"
              description="Voir la carte pricing en bas de page."
              testId="mandat-section-pricing"
            />

            <SectionCard
              icon={Megaphone}
              title="Publications"
              milestone="M4"
              description="Voir la carte publications ci-dessous."
              testId="mandat-section-publications"
            />

            <SectionCard
              icon={LineChart}
              title="Suivi hebdomadaire"
              milestone="M5"
              description="Voir la carte performance ci-dessous."
              testId="mandat-section-monitoring"
            />

            <SectionCard
              icon={MessageCircle}
              title="Échanges vendeur"
              milestone="M8 · à venir"
              description="Messagerie contextualisée avec le vendeur."
              testId="mandat-section-messages"
            />
          </div>

          {/* M3 · Pricing */}
          <div className="mt-10">
            <PricingCard
              pricing={mandate.pricing}
              initialized={mandate.pricing_initialized}
              isTerminal={mandate.is_terminal}
              onInitialize={() => setInitPricingOpen(true)}
              onUpdateAsking={() => setUpdateAskingOpen(true)}
            />
          </div>

          {(pricingHistory.length > 0 || pricingHistoryLoading) && (
            <section
              className="mt-8 rounded-3xl bg-white/60 p-7"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
              data-testid="mandat-pricing-history-card"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
                    <Euro size={16} strokeWidth={1.7} className="text-black/60" />
                  </div>
                  <div>
                    <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-black">
                      Historique pricing
                    </h2>
                    <p className="mt-1 text-[12.5px] text-black/50">
                      Journal append-only des mutations de prix affiché et net vendeur.
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
                  M3
                </span>
              </div>
              <div className="mt-6">
                <PricingHistoryTimeline
                  events={pricingHistory}
                  loading={pricingHistoryLoading}
                />
              </div>
            </section>
          )}

          {/* M4 · Publications */}
          <div className="mt-8">
            <PublicationsCard
              publications={mandate.publications}
              mandateStatus={mandate.status}
              isTerminal={mandate.is_terminal}
              onAddChannel={() => setAddChannelOpen(true)}
              onActivate={doActivateChannel}
              onDeactivate={doDeactivateChannel}
            />
          </div>

          {(pubEvents.length > 0 || pubEventsLoading) && (
            <section
              className="mt-8 rounded-3xl bg-white/60 p-7"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
              data-testid="mandat-publication-events-card"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
                    <Megaphone size={16} strokeWidth={1.7} className="text-black/60" />
                  </div>
                  <div>
                    <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-black">
                      Historique publications
                    </h2>
                    <p className="mt-1 text-[12.5px] text-black/50">
                      Journal événementiel append-only des canaux (ajout, activation, retrait…).
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
                  M4
                </span>
              </div>
              <div className="mt-6">
                <PublicationEventsTimeline
                  events={pubEvents}
                  loading={pubEventsLoading}
                />
              </div>
            </section>
          )}

          {/* M5 · Performance */}
          <div className="mt-8">
            <PerformanceCard
              summary={perfSummary}
              isTerminal={mandate.is_terminal}
              hasPublished={!!mandate.publications?.first_published_at}
              onNewSnapshot={() => setSnapshotOpen(true)}
              onChangeFrequency={() => setFreqDialogOpen(true)}
            />
          </div>

          {(perfSnapshots.length > 0 || perfLoading) && (
            <section
              className="mt-8 rounded-3xl bg-white/60 p-7"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
              data-testid="mandat-performance-history-card"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
                    <LineChart size={16} strokeWidth={1.7} className="text-black/60" />
                  </div>
                  <div>
                    <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-black">
                      Historique snapshots
                    </h2>
                    <p className="mt-1 text-[12.5px] text-black/50">
                      Périodes hebdomadaires immuables · révisions cumulées · deltas S vs S-1.
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
                  M5
                </span>
              </div>
              <div className="mt-6">
                <PerformanceTimeline
                  snapshots={perfSnapshots}
                  loading={perfLoading}
                  onSupersede={(snap) => setSupersedeTarget(snap)}
                  isTerminal={mandate.is_terminal}
                />
              </div>
            </section>
          )}

          {/* Journal M2 · audit */}
          <section
            className="mt-10 rounded-3xl bg-white/60 p-7"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
            data-testid="mandat-audit-card"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
                  <History size={16} strokeWidth={1.7} className="text-black/60" />
                </div>
                <div>
                  <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-black">
                    Journal des transitions
                  </h2>
                  <p className="mt-1 text-[12.5px] text-black/50 max-w-[420px]">
                    Historique horodaté et signé de chaque évolution du mandat.
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
                M2
              </span>
            </div>
            <div className="mt-6">
              <AuditTimeline events={audit} loading={auditLoading} />
            </div>
          </section>

          <div className="h-24" />
        </div>
      </main>

      <CloseMandateDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        onSubmit={doClose}
      />
      <InitializePricingDialog
        open={initPricingOpen}
        onOpenChange={setInitPricingOpen}
        onSubmit={doInitializePricing}
      />
      <UpdateAskingPriceDialog
        open={updateAskingOpen}
        onOpenChange={setUpdateAskingOpen}
        pricing={mandate.pricing}
        onSubmit={doUpdateAsking}
      />
      <AddChannelDialog
        open={addChannelOpen}
        onOpenChange={setAddChannelOpen}
        catalog={pubCatalog}
        existingSlugs={(mandate.publications?.channels || []).map(c => c.channel)}
        onSubmit={doAddChannel}
      />
      <SnapshotDialog
        open={snapshotOpen}
        onOpenChange={setSnapshotOpen}
        mode="create"
        periodInfo={perfSummary?.current_period}
        activeChannels={(mandate.publications?.channels || []).filter(c => c.status === "active")}
        onSubmit={doCreateSnapshot}
      />
      <SnapshotDialog
        open={!!supersedeTarget}
        onOpenChange={(o) => !o && setSupersedeTarget(null)}
        mode="supersede"
        periodInfo={supersedeTarget ? {
          period_index: supersedeTarget.period_index,
          period_start: supersedeTarget.period_start,
          period_end: supersedeTarget.period_end,
        } : null}
        currentRevision={supersedeTarget?.revision}
        activeChannels={(mandate.publications?.channels || []).filter(c => c.status === "active")}
        initialByPlatform={supersedeTarget?.by_platform}
        onSubmit={doSupersedeSnapshot}
      />
      <UpdateFrequencyDialog
        open={freqDialogOpen}
        onOpenChange={setFreqDialogOpen}
        currentDays={perfSummary?.review_frequency_days || mandate.performance?.review_frequency_days || 7}
        onSubmit={doUpdateFrequency}
      />
    </div>
  );
}
